import { CommonModule } from '@angular/common';
import { Component, ContentChild, HostBinding, Input, TemplateRef, ViewEncapsulation } from '@angular/core';

export type UiMessagesSeverity = 'success' | 'info' | 'warn' | 'error' | 'secondary' | 'contrast' | string;

/**
 * Minimal replacement for PrimeNG `p-messages` when used with a single `ng-template` content.
 * Keeps PrimeNG DOM/classes so existing styling stays intact.
 */
@Component({
    selector: 'app-messages',
    standalone: true,
    imports: [CommonModule],
    templateUrl: './app-messages.component.html',
    encapsulation: ViewEncapsulation.None,
})
export class AppMessagesComponent {
    /**
     * Severity level of the message container (used when rendering projected template content).
     * @defaultValue 'info'
     */
    @Input() severity: UiMessagesSeverity | null | undefined = 'info';

    /**
     * Inline style of the messages container.
     */
    @Input() style: { [klass: string]: any } | string | null | undefined;

    /**
     * Style class of the messages container.
     */
    @Input() styleClass: string | undefined;

    // In our codebase we only use `<p-messages><ng-template pTemplate>...</ng-template></p-messages>`.
    // ContentChild(TemplateRef) will still pick it up even if it has `pTemplate` attribute.
    @ContentChild(TemplateRef) contentTemplate: TemplateRef<unknown> | null = null;

    // Keep compatibility with deep selectors.
    @HostBinding('class') hostClass = 'p-element';

    get styleObject(): { [klass: string]: any } | null {
        return this.style && typeof this.style === 'object' ? this.style : null;
    }

    get styleString(): string | null {
        return typeof this.style === 'string' ? this.style : null;
    }

    get containerClass(): string {
        return `p-messages p-component${this.styleClass ? ` ${this.styleClass}` : ''}`;
    }

    get contentSeverityClass(): string {
        const severity = (this.severity ?? 'info').toString();
        return `p-message p-message-${severity}`;
    }
}

